import {
  Component,
  Output,
  EventEmitter,
  inject,
  effect,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { AddConnectionDialogData } from '../add-connection-dialog/add-connection-dialog.component';
import { DialogService } from '../../shared/services/dialog/dialog.service';
import { AppStateService } from '../../shared/services/app-state/app-state.service';
import {
  Connection as ApiConnection,
  ObjectStorageConnection,
} from '@app/shared/interfaces';
import { ConnectionsListStore } from './connections-list.store';
import { ConnectionFlowService } from '../../shared/services/connection-flow/connection-flow.service';
import { ConnectionDisplay } from './connection-display.mapper';

export { ConnectionDisplay } from './connection-display.mapper';

/**
 * ConnectionsComponent — displays and manages a project's connections.
 *
 * A thin OnPush view over {@link ConnectionsListStore}: the store owns all data
 * loading and the display rows (signals/`resource()`), while this component only
 * renders, dispatches UI dialogs (confirm/edit/test), and re-emits the
 * view/edit/delete/count outputs the parent surfaces listen to.
 *
 * Usage:
 * ```html
 * <app-connections
 *   [projectId]="currentProjectId"
 *   (editConnection)="onEditConnection($event)"
 *   (deleteConnection)="onDeleteConnection($event)">
 * </app-connections>
 * ```
 */
@Component({
  selector: 'app-connections',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatDividerModule,
  ],
  templateUrl: './connections.component.html',
  styleUrls: ['./connections.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConnectionsListStore],
})
export class ConnectionsComponent {
  readonly projectId = input<number | null>(null);
  readonly fullWidth = input<boolean>(false);
  /** When true, displays mock connection data instead of loading from API. */
  readonly useMockData = input<boolean>(false);

  @Output() viewConnection = new EventEmitter<ConnectionDisplay>();
  @Output() editConnection = new EventEmitter<ConnectionDisplay>();
  @Output() deleteConnection = new EventEmitter<ConnectionDisplay>();
  @Output() connectionsCountChange = new EventEmitter<number>();

  private readonly store = inject(ConnectionsListStore);
  private readonly dialogService = inject(DialogService);
  private readonly connectionFlow = inject(ConnectionFlowService);
  private readonly appState = inject(AppStateService);

  /** Edit, duplicate and delete are admin-only (EHQ-427); Test is not. */
  readonly canManageConnections = this.appState.canManageConnections;

  /** Display rows (retains previous rows during refetch). */
  readonly displayConnections = this.store.displayConnections;
  readonly loading = this.store.loading;

  constructor() {
    // Push the component inputs into the store, which drives the resources.
    effect(() => this.store.setProjectId(this.projectId()));
    effect(() => this.store.setUseMockData(this.useMockData()));
    // Mirror the total count to the parent.
    effect(() => this.connectionsCountChange.emit(this.store.count()));
  }

  onActionMenu(connection: ConnectionDisplay, action: string): void {
    // The menu hides these from non-admins; refuse them here too, since the
    // server would answer 403 anyway (EHQ-427).
    const adminOnly = action === 'edit' || action === 'delete' || action === 'duplicate';
    if (adminOnly && !this.canManageConnections()) return;
    switch (action) {
      case 'view':
        this.onView(connection);
        break;
      case 'edit':
        this.onEdit(connection);
        break;
      case 'test':
        this.testConnection(connection);
        break;
      case 'delete':
        this.onDelete(connection);
        break;
      case 'duplicate':
        this.duplicateConnection(connection);
        break;
    }
  }

  /** Emit for parent and open the edit dialog. */
  onEdit(connection: ConnectionDisplay): void {
    this.editConnection.emit(connection);
    void this.openEditConnectionDialog(connection);
  }

  /** Emit for parent to open view/details. */
  onView(connection: ConnectionDisplay): void {
    this.viewConnection.emit(connection);
  }

  onDelete(connection: ConnectionDisplay): void {
    if (!connection.originalData.connectionId) {
      console.error('Connection ID is missing');
      return;
    }
    if (!this.store.projectId()) {
      console.warn('No valid project ID available for deleting connection');
      return;
    }

    this.dialogService
      .confirm({
        title: 'Delete Connection',
        message: `Are you sure you want to delete the connection "${connection.name}"? This action cannot be undone.`,
        confirmCaption: 'Delete',
        cancelCaption: 'Cancel',
        isDestructive: true,
      })
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.store
          .deleteConnection(connection)
          .then(() => this.deleteConnection.emit(connection))
          .catch((error) =>
            console.error('Error deleting connection:', error),
          );
      });
  }

  refreshConnections(): void {
    this.store.reload();
  }

  /** Whether the row's type supports duplication (object-storage/unknown don't). */
  canDuplicate(connection: ConnectionDisplay): boolean {
    return (
      this.canManageConnections() &&
      connection.type !== 'objectstorage' &&
      connection.type !== 'unknown'
    );
  }

  /**
   * Run the shared add-connection flow (add dialog → success → optional test)
   * and refresh the list when a connection is created. Exposed so any surface
   * embedding this component (sidebar, list dialog, settings tab) triggers the
   * same flow rather than reimplementing it.
   */
  addConnection(): void {
    if (this.useMockData()) {
      console.warn('Add is disabled while using mock data');
      return;
    }
    const projectId = this.store.projectId();
    if (!projectId) {
      console.warn('No valid project ID available for adding connection');
      return;
    }
    this.connectionFlow.addConnection(projectId).subscribe((created) => {
      if (created) {
        this.store.reload();
      }
    });
  }

  /**
   * Test a saved connection by id through the testing dialog. Object storage
   * goes through the same endpoint: it needs only READ, while the unsaved
   * object-storage test is admin-only (EHQ-427).
   */
  private testConnection(connection: ConnectionDisplay): void {
    const projectId = this.store.projectId();
    if (!projectId) {
      console.warn('No valid project ID available for testing connection');
      return;
    }
    const connectionId = connection.originalData.connectionId;
    if (!connectionId) {
      console.error('Connection ID is missing for test');
      return;
    }

    this.dialogService
      .connectionTestingDialog({
        connectionName: connection.name,
        projectId,
        connectionId,
      })
      .subscribe();
  }

  private duplicateConnection(connection: ConnectionDisplay): void {
    if (connection.type === 'objectstorage' || connection.type === 'unknown') {
      console.warn('Duplicate is not supported for this connection type');
      return;
    }
    if (!this.store.projectId()) {
      console.warn('No valid project ID available for duplicating connection');
      return;
    }
    this.store
      .duplicateConnection(connection)
      .catch((error) => console.error('Error duplicating connection:', error));
  }

  private async openEditConnectionDialog(
    connection: ConnectionDisplay,
  ): Promise<void> {
    if (this.useMockData()) {
      console.warn('Edit is disabled while using mock data');
      return;
    }
    const projectId = this.store.projectId();
    if (!projectId) {
      console.warn('Missing data for edit dialog');
      return;
    }

    const editable = await this.store.resolveEditableConnection(connection);
    if (!editable) {
      console.warn('Missing data for edit dialog');
      return;
    }
    this.openDialogWithConnection(projectId, editable);
  }

  private openDialogWithConnection(
    projectId: number,
    connection: ApiConnection | ObjectStorageConnection,
  ): void {
    const dialogData: AddConnectionDialogData = {
      projectId,
      mode: 'edit',
      connection,
    };

    const dialog$ = this.dialogService.addConnectionDialog(dialogData);
    if (
      !dialog$ ||
      typeof (dialog$ as { subscribe?: unknown }).subscribe !== 'function'
    ) {
      console.warn('Edit dialog did not return an observable result');
      return;
    }

    dialog$.subscribe((result) => {
      const saveResult = result as
        | { connection?: { connectionId?: number } }
        | undefined;
      if (saveResult?.connection?.connectionId) {
        this.store.reload();
      }
    });
  }
}
